/* eslint-disable import/no-cycle */
import React from 'react';
import PropTypes from 'prop-types';
import { Anchor } from '@mantine/core';
import AppFlexbox from '../../../../common/AppFlexbox';
import AppStack from '../../../../common/AppStack';
import AppText from '../../../../common/AppText';
import AppCard from '../../../../common/AppCard';
import WsAccountOrderSummaryProduct from './WsAccountOrderSummaryProduct';
import ResponsiveWebstoreModal from '../../ResponsiveWebstoreModal';
import { useMediaQueryIndex } from '../../../../../helpers/hooks';

const WsAccountOrderProductTrackingModal = ({
  isOpen,
  ecomOrder,
  ecomOrderProduct,
  onClose,
  baseStoreUrl,
  onNavigate,
  mediaQueries,
  editModeEnabled
}) => {
  const { isTabletOrSmaller } = mediaQueries || useMediaQueryIndex();

  return (
    <ResponsiveWebstoreModal
      alignModal={isTabletOrSmaller ? 'end' : 'center'}
      editModeEnabled={editModeEnabled}
      isOpen={isOpen}
      mediaQueries={mediaQueries}
      onClose={onClose}
      radius={0}
      size={700}
      title="Tracking details"
    >
      {ecomOrderProduct && (
        <AppStack style={{ gap: 16 }}>
          <WsAccountOrderSummaryProduct
            addOns={ecomOrderProduct.addOns}
            baseStoreUrl={baseStoreUrl}
            cancelledCount={0}
            count={ecomOrderProduct.quantity - ecomOrderProduct.cancelledCount}
            currency={ecomOrderProduct.currency}
            mediaQueries={mediaQueries}
            onNavigate={onNavigate}
            previewImage={ecomOrderProduct.previewImage}
            productName={ecomOrderProduct.name}
            shippingCount={ecomOrderProduct.shippedCount}
            tax={ecomOrderProduct.tax}
            taxRate={ecomOrderProduct.taxRate}
            unitPrice={ecomOrderProduct.unitCost}
            unitTax={ecomOrderProduct.unitTax}
            urlHandle={ecomOrderProduct.productUrlHandle}
          />

          <AppFlexbox
            style={{
              gap: 16,
              flexDirection: isTabletOrSmaller ? 'column' : 'row'
            }}
          >
            <AppCard radius={8} shadow="none" style={{ flex: 1 }} withBorder>
              <AppStack style={{ gap: 5 }}>
                <AppText style={{ fontSize: 14, fontWeight: 700 }}>
                  Tracking information
                </AppText>
                <AppStack style={{ gap: 0 }}>
                  {ecomOrderProduct.ecomOrderShippingProducts.length === 0 ? (
                    <AppText style={{ fontSize: 14, color: '#666' }}>
                      Tracking detail will show here once the product is
                      shipped.
                    </AppText>
                  ) : ecomOrderProduct.ecomOrderShippingProducts.every(
                      (s) =>
                        !s.ecomOrderShipping.shippingCarrier &&
                        !s.ecomOrderShipping.trackingUrl
                    ) ? (
                    <AppText
                      style={{
                        fontSize: 14,
                        fontStyle: 'italic',
                        color: '#666'
                      }}
                    >
                      No tracking was provided for this shipment
                    </AppText>
                  ) : (
                    <>
                      <AppFlexbox style={{ gap: 8 }}>
                        <AppText style={{ fontSize: 14 }}>
                          Shipped by{' '}
                          {ecomOrderProduct.ecomOrderShippingProducts
                            .map((p) => p.ecomOrderShipping.shippingCarrier)
                            .join(', ')}
                        </AppText>
                      </AppFlexbox>
                      <AppFlexbox style={{ gap: 5 }}>
                        <AppText style={{ fontSize: 14 }}>Tracking ID:</AppText>
                        {ecomOrderProduct.ecomOrderShippingProducts.some(
                          (p) =>
                            !!p.ecomOrderShipping.trackingUrl ||
                            !!p.ecomOrderShipping.trackingNumber
                        ) ? (
                          ecomOrderProduct.ecomOrderShippingProducts
                            .filter(
                              (p) =>
                                !!p.ecomOrderShipping.trackingUrl ||
                                !!p.ecomOrderShipping.trackingNumber
                            )
                            .map((p) => (
                              <Anchor
                                key={p.pkEcomOrderShippingProduct}
                                c={
                                  p.ecomOrderShipping.trackingUrl
                                    ? 'dodgerblue'
                                    : '#000'
                                }
                                href={p.ecomOrderShipping.trackingUrl}
                                style={{ fontSize: 14 }}
                                target="_blank"
                                underline={
                                  p.ecomOrderShipping.trackingUrl
                                    ? 'hover'
                                    : 'none'
                                }
                              >
                                {p.ecomOrderShipping.trackingNumber ||
                                  'View tracking'}
                              </Anchor>
                            ))
                        ) : (
                          <AppText
                            style={{
                              fontSize: 14,
                              fontStyle: 'italic',
                              color: '#666'
                            }}
                          >
                            No further tracking information is available
                          </AppText>
                        )}
                      </AppFlexbox>
                    </>
                  )}
                </AppStack>
              </AppStack>
            </AppCard>
            <AppCard radius={8} shadow="none" style={{ flex: 1 }} withBorder>
              <AppStack style={{ gap: 5 }}>
                <AppText style={{ fontSize: 14, fontWeight: 700 }}>
                  Shipping address
                </AppText>
                {ecomOrderProduct.ecomOrderShippingProducts[0]
                  ?.ecomOrderShipping.ecomCustomerAddress && (
                  <AppStack style={{ gap: 0 }}>
                    <AppText style={{ fontSize: 14 }}>
                      {
                        ecomOrderProduct.ecomOrderShippingProducts[0]
                          .ecomOrderShipping.ecomCustomerAddress.fullName
                      }
                    </AppText>
                    {ecomOrderProduct.ecomOrderShippingProducts[0]
                      .ecomOrderShipping.fkEcomOrder ===
                      ecomOrder.pkEcomOrder && (
                      <>
                        <AppText style={{ fontSize: 14 }}>
                          {
                            ecomOrderProduct.ecomOrderShippingProducts[0]
                              .ecomOrderShipping.ecomCustomerAddress.address1
                          }
                        </AppText>
                        <AppText style={{ fontSize: 14 }}>
                          {
                            ecomOrderProduct.ecomOrderShippingProducts[0]
                              .ecomOrderShipping.ecomCustomerAddress.city
                          }
                          ,{' '}
                          {
                            ecomOrderProduct.ecomOrderShippingProducts[0]
                              .ecomOrderShipping.ecomCustomerAddress.state
                          }{' '}
                          {
                            ecomOrderProduct.ecomOrderShippingProducts[0]
                              .ecomOrderShipping.ecomCustomerAddress.postalCode
                          }
                        </AppText>
                        <AppText style={{ fontSize: 14 }}>
                          {
                            ecomOrderProduct.ecomOrderShippingProducts[0]
                              .ecomOrderShipping.ecomCustomerAddress.country
                          }
                        </AppText>
                      </>
                    )}
                  </AppStack>
                )}
              </AppStack>
            </AppCard>
          </AppFlexbox>
        </AppStack>
      )}
    </ResponsiveWebstoreModal>
  );
};

WsAccountOrderProductTrackingModal.propTypes = {
  baseStoreUrl: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
  ecomOrder: PropTypes.object,
  ecomOrderProduct: PropTypes.object,
  editModeEnabled: PropTypes.bool,
  isOpen: PropTypes.bool,
  mediaQueries: PropTypes.object
};

export default WsAccountOrderProductTrackingModal;
